import { useCallback, useMemo, useState } from 'react';
import { DlpTrackCountType } from '../Types';
import { RootState, useAppSelector } from '../store';
import { dashboardService } from '../Services/dashboardService';

const useComplianceDashboard = () => {
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);
    const demo = useAppSelector((state: RootState) => state.environment.demoInstance as boolean);

    const selectedLanguage = useAppSelector((state: RootState) => state.i18n.selectedLanguage);

    const [timeRange, setTimeRange] = useState<any>({
        from: new Date(new Date().getFullYear(), new Date().getMonth(), 1, 0, 0, 0),
        to: new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate(), 23, 59, 59),
    });

    const dateTimeConverter = useCallback((dateTime: Date) => {
        return dateTime.toISOString().replace(/\.\d{3}/, '');
    }, []);

    const setTimeRangeWithTime = useCallback((value: any) => {
        value.from?.setHours(0, 0, 0, 0);
        if (typeof value.to === 'undefined') {
            value.to = new Date(value.from ?? new Date());
        }
        value.to?.setHours(23, 59, 59, 999);
        setTimeRange(value);
    }, []);

    const DashboardActions = useMemo(
        () => ({
            setTimeRangeWithTime,
        }),
        [setTimeRangeWithTime],
    );

    return {
        DashboardActions,
        timeRange,
        selectedLanguage,
        i18n,
        dlpTrackTooltip: useMemo(
            () =>
                (
                    { sensitiveCount, anonymizedCount, recognizedSensitiveCount }: DlpTrackCountType,
                    recognized = false,
                ) => {
                    if (recognized) {
                        return i18n
                            .translate({ id: 'recognizedAsSensitive', sourceName: 'Dashboard' })
                            .replace('{recognizedSensitiveCount}', recognizedSensitiveCount);
                    }
                    return i18n
                        .translate({ id: 'sensitiveAndAnonymizedSent', sourceName: 'Dashboard' })
                        .replace('{sensitiveAndAnonymizedSentCount}', sensitiveCount + anonymizedCount)
                        .replace('{anonymizedCount}', anonymizedCount);
                },
            [i18n],
        ),
        getDlpTrackCountPerUser: useMemo(
            () => (page: number, itemsPerPage: number, _: any, query?: string | null) => {
                if (timeRange.from && timeRange.to) {
                    return dashboardService.getDlpTrackCountPerUser(
                        demo,
                        dateTimeConverter(timeRange.from),
                        dateTimeConverter(timeRange.to),
                        page,
                        itemsPerPage,
                        query,
                    );
                }
            },
            [demo, dateTimeConverter, timeRange.from, timeRange.to],
        ),
        getDlpTrackCountPerGroup: useMemo(
            () => (page: number, itemsPerPage: number, _: any, query?: string | null) => {
                if (timeRange.from && timeRange.to) {
                    return dashboardService.getDlpTrackCountPerGroup(
                        demo,
                        dateTimeConverter(timeRange.from),
                        dateTimeConverter(timeRange.to),
                        page,
                        itemsPerPage,
                        query,
                    );
                }
            },
            [demo, dateTimeConverter, timeRange.from, timeRange.to],
        ),
    };
};

export default useComplianceDashboard;
